/** SPEC §21: Beheer › Instellingen › API en webhooks. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WEBHOOK_GEBEURTENISSEN, WEBHOOK_LABEL } from '@ep/core';
import { api } from '../lib/api';
import { Icoon } from '../ui/Icoon';
import { toast } from '../ui/toast';
import { Badge, Laden, Modal, Veld, VerwijderKnop } from '../ui/ui';

interface Sleutel { id: string; naam: string; begin: string; recht: 'LEZEN' | 'SCHRIJVEN'; door: string; laatstGebruikt: string | null; aangemaakt: string }
interface Webhook { id: string; url: string; gebeurtenissen: string[]; actief: boolean; mislukt: number; melding: string | null; door: string; aangemaakt: string; week: Record<string, number> }
interface Bericht { id: string; gebeurtenis: string; status: 'WACHT' | 'OK' | 'MISLUKT'; pogingen: number; code: number | null; fout: string | null; aangemaakt: string; verstuurd: string | null; volgende: string }

const tijd = (t: string | null) => (t ? new Date(t).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '–');
const kopieer = async (t: string) => {
  try {
    await navigator.clipboard.writeText(t);
    toast('Gekopieerd', 'succes');
  } catch {
    toast('Kopiëren lukt niet; selecteer de tekst en kopieer zelf', 'fout');
  }
};

function Geheim({ titel, waarde, uitleg, onSluit }: { titel: string; waarde: string; uitleg: string; onSluit: () => void }) {
  return (
    <Modal titel={titel} onSluit={onSluit} voet={<><div className="groei" /><button className="knop primair" onClick={onSluit}>Ik heb hem bewaard</button></>}>
      <p className="klein">{uitleg}</p>
      <div className="sleutel-toon">
        <span className="groei">{waarde}</span>
        <button className="knop klein" onClick={() => void kopieer(waarde)}><Icoon naam="copy" maat={14} /> Kopiëren</button>
      </div>
      <p className="klein mute">Deze waarde is maar één keer zichtbaar. Bewaar hem in een wachtwoordkluis of in de instellingen van de koppeling; zet hem niet in een mail of chat.</p>
    </Modal>
  );
}

export function ApiBeheer() {
  const qc = useQueryClient();
  const sl = useQuery({ queryKey: ['api-sleutels'], queryFn: () => api.get<Sleutel[]>('/api/api-sleutels') });
  const wh = useQuery({ queryKey: ['webhooks'], queryFn: () => api.get<Webhook[]>('/api/webhooks'), refetchInterval: 30000 });
  const [nieuweSleutel, setNieuweSleutel] = useState<{ naam: string; recht: 'LEZEN' | 'SCHRIJVEN' } | null>(null);
  const [geheim, setGeheim] = useState<{ titel: string; waarde: string; uitleg: string } | null>(null);
  const [hook, setHook] = useState<Partial<Webhook> | null>(null);
  const [log, setLog] = useState<Webhook | null>(null);
  const doe = async (f: () => Promise<unknown>, melding?: string) => {
    try {
      const r = await f();
      await Promise.all([qc.invalidateQueries({ queryKey: ['api-sleutels'] }), qc.invalidateQueries({ queryKey: ['webhooks'] })]);
      if (melding) toast(melding, 'succes');
      return r;
    } catch (e) {
      toast((e as Error).message, 'fout');
      return null;
    }
  };
  const basis = window.location.origin;
  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1100 }}>
      <div className="paneel">
        <h1>API en webhooks</h1>
        <p className="klein mute" style={{ marginTop: 0 }}>
          Koppel bsmartr aan andere systemen. Met een <b>API-sleutel</b> lees of wijzig je projecten, taken, werkpakketten, engineers, afwezigheid en uren via <code>{basis}/api/v1</code>. Met een <b>webhook</b> krijgt een ander systeem direct bericht als er iets gebeurt. Werkt met Power Automate, Zapier, Make en eigen software.
        </p>
        <div className="rij wrap">
          <a className="knop" href="/api/v1/swagger.json" download="bsmartr-swagger.json"><Icoon naam="download" maat={16} /> Swagger (OpenAPI 2.0)</a>
          <span className="klein mute">Power Automate: <i>Aangepaste connectors › Nieuwe › OpenAPI-bestand importeren</i>, beveiliging <i>API-sleutel</i> in de kop <code>Authorization</code> met waarde <code>bsk_…</code> (met of zonder <code>Bearer</code> ervoor).</span>
        </div>
      </div>

      <div className="paneel">
        <div className="sectie-kop">
          <h2 className="groei"><Icoon naam="key-round" maat={18} /> API-sleutels</h2>
          <button className="knop" onClick={() => setNieuweSleutel({ naam: '', recht: 'LEZEN' })}><Icoon naam="plus" maat={16} /> Sleutel</button>
        </div>
        {!sl.data ? <Laden /> : sl.data.length ? (
          <table className="tabel">
            <thead><tr><th>Naam</th><th>Sleutel</th><th>Recht</th><th>Laatst gebruikt</th><th>Gemaakt door</th><th /></tr></thead>
            <tbody>
              {sl.data.map((k) => (
                <tr key={k.id}>
                  <td className="vet">{k.naam}</td>
                  <td><code className="klein">{k.begin}…</code></td>
                  <td>{k.recht === 'SCHRIJVEN' ? <Badge toon="oranje">lezen en schrijven</Badge> : <Badge toon="grijs">alleen lezen</Badge>}</td>
                  <td className="klein">{tijd(k.laatstGebruikt)}</td>
                  <td className="klein mute">{k.door} · {tijd(k.aangemaakt)}</td>
                  <td><VerwijderKnop klein label="Intrekken" onBevestig={() => void doe(() => api.del(`/api/api-sleutels/${k.id}`), 'Sleutel ingetrokken')} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="klein mute">Nog geen API-sleutels.</div>}
        <details className="klein" style={{ marginTop: 10 }}>
          <summary>Voorbeeld</summary>
          <div className="code-blok">{`curl ${basis}/api/v1/taken?van=2026-11-01 \\\n  -H "Authorization: Bearer bsk_…"`}</div>
          <div className="code-blok">{`curl -X POST ${basis}/api/v1/taken \\\n  -H "Authorization: Bearer bsk_…" -H "Content-Type: application/json" \\\n  -d '{"titel":"Storing printer","type":"SERVICE_CALL","engineerId":"…","startdatum":"2026-11-10","velden":{"kostenplaats":"4010"}}'`}</div>
        </details>
      </div>

      <div className="paneel">
        <div className="sectie-kop">
          <h2 className="groei"><Icoon naam="webhook" maat={18} /> Webhooks</h2>
          <button className="knop" onClick={() => setHook({ url: '', gebeurtenissen: ['taak.aangemaakt', 'taak.gewijzigd'] })}><Icoon naam="plus" maat={16} /> Webhook</button>
        </div>
        {!wh.data ? <Laden /> : wh.data.length ? (
          <table className="tabel">
            <thead><tr><th>URL</th><th>Gebeurtenissen</th><th>Status</th><th>Laatste 7 dagen</th><th /></tr></thead>
            <tbody>
              {wh.data.map((w) => (
                <tr key={w.id} className="klik" onClick={() => setHook(w)}>
                  <td style={{ maxWidth: 320, wordBreak: 'break-all' }}>{w.url}</td>
                  <td className="klein">{w.gebeurtenissen.map((g) => WEBHOOK_LABEL[g as keyof typeof WEBHOOK_LABEL] ?? g).join(', ')}</td>
                  <td>
                    {w.actief ? <Badge toon="groen">aan</Badge> : <Badge toon="rood">uit</Badge>}
                    {w.melding && <div className="klein" style={{ color: 'var(--rood)' }} title={w.melding}>{w.melding.slice(0, 60)}</div>}
                  </td>
                  <td className="klein nowrap">{w.week.OK ?? 0} gelukt · {w.week.WACHT ?? 0} wacht · {w.week.MISLUKT ?? 0} mislukt</td>
                  <td className="nowrap" onClick={(e) => e.stopPropagation()}>
                    <button className="knop klein" onClick={() => void doe(() => api.post(`/api/webhooks/${w.id}/test`), 'Testbericht verstuurd; bekijk het resultaat in het logboek')}>Test</button>{' '}
                    <button className="knop klein" onClick={() => setLog(w)}>Logboek</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="klein mute">Nog geen webhooks.</div>}
        <details className="klein" style={{ marginTop: 10 }}>
          <summary>Bericht en handtekening controleren</summary>
          <p>Elk bericht is een <code>POST</code> met JSON <code>{'{ id, gebeurtenis, tijd, organisatie, data }'}</code> en de kop <code>X-Bsmartr-Handtekening: t=…,v1=…</code>. Controleer <code>v1</code> = HMAC-SHA256 van <code>{'"<t>.<body>"'}</code> met het geheim, en weiger berichten waarvan <code>t</code> ouder is dan 5 minuten. Antwoord met een 2xx-code; anders proberen we het opnieuw na 1 min, 5 min, 30 min, 2 uur, 6 uur en 24 uur.</p>
          <div className="code-blok">{`// Node.js
const [t, v1] = kop.match(/t=(\\d+),v1=([0-9a-f]+)/).slice(1);
const ok = crypto.createHmac('sha256', geheim).update(\`\${t}.\${ruweBody}\`).digest('hex') === v1;`}</div>
        </details>
      </div>

      {nieuweSleutel && (
        <Modal
          titel="Nieuwe API-sleutel"
          onSluit={() => setNieuweSleutel(null)}
          voet={
            <>
              <div className="groei" />
              <button className="knop" onClick={() => setNieuweSleutel(null)}>Annuleren</button>
              <button
                className="knop primair"
                disabled={!nieuweSleutel.naam.trim()}
                onClick={async () => {
                  const r = (await doe(() => api.post<{ sleutel: string; naam: string }>('/api/api-sleutels', nieuweSleutel))) as { sleutel: string; naam: string } | null;
                  if (r) {
                    setNieuweSleutel(null);
                    setGeheim({ titel: `API-sleutel ${r.naam}`, waarde: r.sleutel, uitleg: 'Gebruik deze sleutel in de kop Authorization: Bearer <sleutel>.' });
                  }
                }}
              >
                Aanmaken
              </button>
            </>
          }
        >
          <div className="grid2">
            <Veld label="Naam *" hint="Waarvoor, bijv. Power Automate of Rapportage"><input autoFocus value={nieuweSleutel.naam} onChange={(e) => setNieuweSleutel({ ...nieuweSleutel, naam: e.target.value })} /></Veld>
            <Veld label="Recht">
              <select value={nieuweSleutel.recht} onChange={(e) => setNieuweSleutel({ ...nieuweSleutel, recht: e.target.value as 'LEZEN' })}>
                <option value="LEZEN">Alleen lezen</option>
                <option value="SCHRIJVEN">Lezen en schrijven</option>
              </select>
            </Veld>
          </div>
          <p className="klein mute">Een sleutel werkt als beheerder van deze organisatie, maar alleen op de openbare API. Alles wat hij wijzigt staat in het audit-log op zijn naam.</p>
        </Modal>
      )}

      {hook && <WebhookForm w={hook} onSluit={() => setHook(null)} doe={doe} onGeheim={(g) => setGeheim({ titel: 'Geheim van de webhook', waarde: g, uitleg: 'Met dit geheim controleer je de handtekening (X-Bsmartr-Handtekening) van elk bericht.' })} />}
      {log && <WebhookLog w={log} onSluit={() => setLog(null)} />}
      {geheim && <Geheim {...geheim} onSluit={() => setGeheim(null)} />}
    </div>
  );
}

function WebhookForm({ w, onSluit, doe, onGeheim }: { w: Partial<Webhook>; onSluit: () => void; doe: (f: () => Promise<unknown>, m?: string) => Promise<unknown>; onGeheim: (g: string) => void }) {
  const [url, setUrl] = useState(w.url ?? '');
  const [ev, setEv] = useState<string[]>(w.gebeurtenissen ?? []);
  const [actief, setActief] = useState(w.actief ?? true);
  const opslaan = async () => {
    if (w.id) {
      if (await doe(() => api.patch(`/api/webhooks/${w.id}`, { url, gebeurtenissen: ev, actief }), 'Webhook opgeslagen')) onSluit();
    } else {
      const r = (await doe(() => api.post<{ geheim: string }>('/api/webhooks', { url, gebeurtenissen: ev }), 'Webhook aangemaakt')) as { geheim: string } | null;
      if (r) {
        onSluit();
        onGeheim(r.geheim);
      }
    }
  };
  return (
    <Modal
      titel={w.id ? 'Webhook bewerken' : 'Nieuwe webhook'}
      onSluit={onSluit}
      voet={
        <>
          {w.id && <VerwijderKnop onBevestig={async () => { if (await doe(() => api.del(`/api/webhooks/${w.id}`), 'Webhook verwijderd')) onSluit(); }} />}
          <div className="groei" />
          <button className="knop" onClick={onSluit}>Annuleren</button>
          <button className="knop primair" disabled={!url.trim() || !ev.length} onClick={() => void opslaan()}>Opslaan</button>
        </>
      }
    >
      <Veld label="URL *" hint="Alleen https://. Bijv. de URL van een Power Automate-trigger ‘Wanneer een HTTP-aanvraag wordt ontvangen’ of ‘Webhooks by Zapier’."><input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" autoFocus /></Veld>
      <div className="klein vet" style={{ margin: '10px 0 4px' }}>Gebeurtenissen</div>
      <div className="grid2">
        {WEBHOOK_GEBEURTENISSEN.map((g) => (
          <label key={g} className="check">
            <input type="checkbox" checked={ev.includes(g)} onChange={(e) => setEv(e.target.checked ? [...ev, g] : ev.filter((x) => x !== g))} /> {WEBHOOK_LABEL[g]} <code className="klein mute">{g}</code>
          </label>
        ))}
      </div>
      {w.id && <label className="check" style={{ marginTop: 10 }}><input type="checkbox" checked={actief} onChange={(e) => setActief(e.target.checked)} /> Aan {w.mislukt ? <span className="klein mute">({w.mislukt} mislukt op rij)</span> : null}</label>}
    </Modal>
  );
}

function WebhookLog({ w, onSluit }: { w: Webhook; onSluit: () => void }) {
  const q = useQuery({ queryKey: ['webhook-log', w.id], queryFn: () => api.get<Bericht[]>(`/api/webhooks/${w.id}/berichten`), refetchInterval: 5000 });
  return (
    <Modal titel="Logboek webhook" breedte="breed" onSluit={onSluit}>
      <div className="klein mute" style={{ wordBreak: 'break-all', marginBottom: 8 }}>{w.url}</div>
      {!q.data ? <Laden /> : (
        <table className="tabel">
          <thead><tr><th>Tijd</th><th>Gebeurtenis</th><th>Status</th><th>Code</th><th>Pogingen</th><th>Fout / volgende poging</th></tr></thead>
          <tbody>
            {q.data.map((b) => (
              <tr key={b.id}>
                <td className="klein nowrap">{tijd(b.aangemaakt)}</td>
                <td><code className="klein">{b.gebeurtenis}</code></td>
                <td>{b.status === 'OK' ? <Badge toon="groen">gelukt</Badge> : b.status === 'WACHT' ? <Badge toon="oranje">wacht</Badge> : <Badge toon="rood">mislukt</Badge>}</td>
                <td>{b.code ?? '–'}</td>
                <td>{b.pogingen}</td>
                <td className="klein">{b.fout ?? ''}{b.status === 'WACHT' && b.pogingen > 0 ? ` · opnieuw ${tijd(b.volgende)}` : ''}</td>
              </tr>
            ))}
            {!q.data.length && <tr><td colSpan={6} className="leeg">Nog geen berichten.</td></tr>}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
